import {
  fetchEvents,
  createEvent,
  updateEvent,
  deleteEvent,
  deleteSeriesAll,
  deleteSeriesFrom,
  subscribeToEventUpdates,
} from "../api/eventsApi";
import { fetchBillingStatuses } from "../api/invoicesApi";
import { useState, useEffect, useCallback, useRef } from "react";
import { normalizeEventAttachments } from "../utils/eventAttachments";
import { getAuthToken, getAuthTokenClaims } from "../api/authApi";

const EVENTS_CACHE_PREFIX = "calendarEventsCache";
const LOG_BACKGROUND_LOAD_PERF = false;

function logBackgroundLoadPerf(message, meta = undefined) {
  if (!LOG_BACKGROUND_LOAD_PERF) return;
  if (meta === undefined) {
    console.info(message);
    return;
  }
  console.info(message, meta);
}

function getEventsCacheKey() {
  const claims = getAuthTokenClaims();
  const scope = String(claims?.sub || claims?.user_id || "default").trim() || "default";
  return `${EVENTS_CACHE_PREFIX}:${scope}`;
}

function readCachedEvents() {
  try {
    const raw = window.localStorage.getItem(getEventsCacheKey());
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeCachedEvents(events) {
  try {
    window.localStorage.setItem(getEventsCacheKey(), JSON.stringify(Array.isArray(events) ? events : []));
  } catch {
    // Ignore storage failures so event loading continues.
  }
}

function withTimeout(promise, timeoutMs, fallbackValue = null) {
  return Promise.race([
    promise,
    new Promise((resolve) => {
      window.setTimeout(() => resolve(fallbackValue), timeoutMs);
    }),
  ]);
}

function isDeleteNotFoundError(error) {
  const message = String(error?.message || "");
  return message.includes("API 404") || message.includes('"detail":"Event not found"');
}

function getPerfNowMs() {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }
  return Date.now();
}

function asIntMs(value) {
  return Math.round(Number(value) || 0);
}

export function useEvents(options = {}) {
  const { enabled = true } = options;
  const loadSequenceRef = useRef(0);
  const loadInFlightRef = useRef(null);
  const loadRerunRequestedRef = useRef(false);
  const [events, setEvents] = useState(() => {
    if (!enabled || !getAuthToken()) return [];
    return readCachedEvents();
  });

  const normalizeEventRecord = useCallback((e, billingInfo = null) => ({
    id: e.id,
    title: e.title,
    start: e.start,
    end: e.end,
    extendedProps: {
      create_request_id: e.extendedProps?.create_request_id ?? "",
      project_id: e.extendedProps?.project_id ?? null,
      project_name: e.extendedProps?.project_name ?? "",
      is_lesson: e.extendedProps?.is_lesson ?? false,
      no_show: e.extendedProps?.no_show ?? false,
      fb_time_entry_id: e.extendedProps?.fb_time_entry_id ?? "",
      invoice_notes: e.extendedProps?.invoice_notes ?? "",
      internal_notes: e.extendedProps?.internal_notes ?? "",
      location: e.extendedProps?.location ?? "",
      category_id: e.extendedProps?.category_id ?? "",
      category_name: e.extendedProps?.category_name ?? "",
      category_color: e.extendedProps?.category_color ?? "",
      series_id: e.extendedProps?.series_id ?? "",
      repeat_rule: e.extendedProps?.repeat_rule ?? "",
      repeat_until: e.extendedProps?.repeat_until ?? "",
      repeat_end_mode: e.extendedProps?.repeat_end_mode ?? "",
      repeat_periods: e.extendedProps?.repeat_periods ?? 3,
      billing_status: e.extendedProps?.billing_status ?? billingInfo?.billing_status ?? "",
      billing_invoice_id: e.extendedProps?.billing_invoice_id ?? billingInfo?.invoice_id ?? "",
      billing_invoice_number: e.extendedProps?.billing_invoice_number ?? billingInfo?.invoice_number ?? "",
      billing_payment_status: e.extendedProps?.billing_payment_status ?? billingInfo?.payment_status ?? "",
      billing_v3_status: e.extendedProps?.billing_v3_status ?? billingInfo?.v3_status ?? "",
      reminder_enabled: e.extendedProps?.reminder_enabled ?? false,
      reminder_config: e.extendedProps?.reminder_config ?? "",
      attachments: normalizeEventAttachments(e.extendedProps?.attachments),
    },
  }), []);

  const applyBillingStatuses = useCallback((currentEvents, billingPayload) => {
    const billingMap = new Map(
      Array.isArray(billingPayload?.statuses)
        ? billingPayload.statuses.map((item) => [String(item?.fb_time_entry_id || ""), item])
        : []
    );

    if (billingMap.size === 0) {
      return currentEvents;
    }

    return currentEvents.map((eventItem) => {
      const billingInfo = billingMap.get(String(eventItem.extendedProps?.fb_time_entry_id || ""));
      if (!billingInfo) {
        return eventItem;
      }

      return {
        ...eventItem,
        extendedProps: {
          ...eventItem.extendedProps,
          billing_status: billingInfo?.billing_status ?? eventItem.extendedProps?.billing_status ?? "",
          billing_invoice_id: billingInfo?.invoice_id ?? eventItem.extendedProps?.billing_invoice_id ?? "",
          billing_invoice_number: billingInfo?.invoice_number ?? eventItem.extendedProps?.billing_invoice_number ?? "",
          billing_payment_status: billingInfo?.payment_status ?? eventItem.extendedProps?.billing_payment_status ?? "",
          billing_v3_status: billingInfo?.v3_status ?? eventItem.extendedProps?.billing_v3_status ?? "",
        },
      };
    });
  }, []);

  const hydrateBillingStatuses = useCallback(async (sequenceId, loadStartedAt = getPerfNowMs()) => {
    const billingStartedAt = getPerfNowMs();
    const billingPayload = await withTimeout(fetchBillingStatuses().catch(() => null), 2500, null);
    if (!billingPayload) {
      const elapsedMs = asIntMs(getPerfNowMs() - billingStartedAt);
      logBackgroundLoadPerf(`[perf] useEvents.load billing.hydrate.skipped +${elapsedMs}ms`, {
        sequenceId,
        reason: "timeout-or-error",
      });
      return;
    }

    if (sequenceId !== loadSequenceRef.current) {
      const elapsedMs = asIntMs(getPerfNowMs() - billingStartedAt);
      logBackgroundLoadPerf(`[perf] useEvents.load billing.hydrate.stale +${elapsedMs}ms`, {
        sequenceId,
        activeSequenceId: loadSequenceRef.current,
      });
      return;
    }

    setEvents((currentEvents) => {
      const withBilling = applyBillingStatuses(currentEvents, billingPayload);
      writeCachedEvents(withBilling);
      return withBilling;
    });
    const elapsedMs = asIntMs(getPerfNowMs() - billingStartedAt);
    const sinceLoadStartMs = asIntMs(getPerfNowMs() - loadStartedAt);
    logBackgroundLoadPerf(`[perf] useEvents.load billing.hydrate.applied +${elapsedMs}ms (${sinceLoadStartMs}ms since load start)`, {
      sequenceId,
      statusCount: Array.isArray(billingPayload?.statuses) ? billingPayload.statuses.length : 0,
    });
  }, [applyBillingStatuses]);

  const runSingleLoad = useCallback(async () => {
    if (!enabled) {
      setEvents([]);
      return;
    }

    if (!getAuthToken()) {
      setEvents([]);
      return;
    }

    const sequenceId = loadSequenceRef.current + 1;
    loadSequenceRef.current = sequenceId;
    const loadStartedAt = getPerfNowMs();
    logBackgroundLoadPerf("[perf] useEvents.load started", { sequenceId });

    try {
      const fetchStartedAt = getPerfNowMs();
      const data = await fetchEvents();
      const fetchMs = asIntMs(getPerfNowMs() - fetchStartedAt);
      const normalised = data.map((eventItem) => normalizeEventRecord(eventItem));
      setEvents(normalised);
      writeCachedEvents(normalised);
      const totalMs = asIntMs(getPerfNowMs() - loadStartedAt);
      logBackgroundLoadPerf(`[perf] useEvents.load events.applied +${fetchMs}ms (${totalMs}ms total)`, {
        sequenceId,
        count: normalised.length,
      });
      void hydrateBillingStatuses(sequenceId, loadStartedAt);
    } catch (firstError) {
      try {
        await new Promise((resolve) => window.setTimeout(resolve, 700));
        const retryFetchStartedAt = getPerfNowMs();
        const data = await fetchEvents();
        const retryFetchMs = asIntMs(getPerfNowMs() - retryFetchStartedAt);
        const normalised = data.map((eventItem) => normalizeEventRecord(eventItem));
        setEvents(normalised);
        writeCachedEvents(normalised);
        const totalMs = asIntMs(getPerfNowMs() - loadStartedAt);
        logBackgroundLoadPerf(`[perf] useEvents.load events.applied.retry +${retryFetchMs}ms (${totalMs}ms total)`, {
          sequenceId,
          count: normalised.length,
        });
        void hydrateBillingStatuses(sequenceId, loadStartedAt);
      } catch (secondError) {
        const cachedEvents = readCachedEvents();
        if (cachedEvents.length > 0) {
          setEvents(cachedEvents);
          const totalMs = asIntMs(getPerfNowMs() - loadStartedAt);
          logBackgroundLoadPerf(`[perf] useEvents.load cache.fallback +${totalMs}ms`, {
            sequenceId,
            count: cachedEvents.length,
          });
          return;
        }
        const totalMs = asIntMs(getPerfNowMs() - loadStartedAt);
        logBackgroundLoadPerf(`[perf] useEvents.load failed +${totalMs}ms`, {
          sequenceId,
          message: String(secondError?.message || secondError),
        });
        throw secondError;
      }
    }
  }, [enabled, hydrateBillingStatuses, normalizeEventRecord]);

  const load = useCallback(async () => {
    if (loadInFlightRef.current) {
      loadRerunRequestedRef.current = true;
      return loadInFlightRef.current;
    }

    const run = (async () => {
      try {
        do {
          loadRerunRequestedRef.current = false;
          await runSingleLoad();
        } while (loadRerunRequestedRef.current);
      } finally {
        loadInFlightRef.current = null;
      }
    })();

    loadInFlightRef.current = run;
    return run;
  }, [runSingleLoad]);

  const mergeEventUpdate = useCallback((currentEvents, id, data) => {
    const nextExtendedProps = data.extendedProps || {};
    return currentEvents.map((eventItem) => {
      if (eventItem.id !== id) return eventItem;
      return normalizeEventRecord({
        ...eventItem,
        ...data,
        id,
        extendedProps: {
          ...eventItem.extendedProps,
          ...nextExtendedProps,
        },
      });
    });
  }, [normalizeEventRecord]);

  const applyRealtimeUpdate = useCallback((previousEvents, update) => {
    if (!update) return previousEvents;

    if (update.type === "delete") {
      const deletedId = String(update.id || "");
      if (!deletedId) return previousEvents;
      return previousEvents.filter((eventItem) => String(eventItem.id) !== deletedId);
    }

    if (update.type === "create" || update.type === "update") {
      if (!update.event?.id) return previousEvents;
      const normalized = normalizeEventRecord(update.event);
      const existingIndex = previousEvents.findIndex((eventItem) => String(eventItem.id) === String(normalized.id));

      if (existingIndex === -1) {
        return [...previousEvents, normalized];
      }

      const next = [...previousEvents];
      next[existingIndex] = normalized;
      return next;
    }

    return previousEvents;
  }, [normalizeEventRecord]);

  const create = async (data) => {
    const startedAt = getPerfNowMs();
    const result = await createEvent(data);
    const createMs = asIntMs(getPerfNowMs() - startedAt);
    console.info(`[perf] useEvents.create mutation.completed +${createMs}ms`);
    await load();
    const totalMs = asIntMs(getPerfNowMs() - startedAt);
    console.info(`[perf] useEvents.create total +${totalMs}ms`);
    return result;
  };

  const createSilently = async (data) => {
    const result = await createEvent(data);
    return result;
  };

  const update = async (id, data) => {
    const startedAt = getPerfNowMs();
    setEvents((currentEvents) => mergeEventUpdate(currentEvents, id, data));
    try {
      const mutationStartedAt = getPerfNowMs();
      await updateEvent(id, data);
      const mutationMs = asIntMs(getPerfNowMs() - mutationStartedAt);
      console.info(`[perf] useEvents.update mutation.completed +${mutationMs}ms`, { id: String(id || "") });
      await load();
      const totalMs = asIntMs(getPerfNowMs() - startedAt);
      console.info(`[perf] useEvents.update total +${totalMs}ms`, { id: String(id || "") });
    } catch (error) {
      await load();
      throw error;
    }
  };

  const updateSilently = async (id, data) => {
    setEvents((currentEvents) => mergeEventUpdate(currentEvents, id, data));
    try {
      await updateEvent(id, data);
    } catch (error) {
      await load();
      throw error;
    }
  };

  const remove = async (id) => {
    const startedAt = getPerfNowMs();
    try {
      const mutationStartedAt = getPerfNowMs();
      await deleteEvent(id);
      const mutationMs = asIntMs(getPerfNowMs() - mutationStartedAt);
      console.info(`[perf] useEvents.remove mutation.completed +${mutationMs}ms`, { id: String(id || "") });
    } catch (error) {
      if (!isDeleteNotFoundError(error)) {
        throw error;
      }
    }

    // Apply deletion immediately so UI doesn't wait on a full reload roundtrip.
    setEvents((currentEvents) => {
      const next = currentEvents.filter((eventItem) => String(eventItem.id) !== String(id));
      writeCachedEvents(next);
      return next;
    });

    void load().catch(() => {});
    const totalMs = asIntMs(getPerfNowMs() - startedAt);
    console.info(`[perf] useEvents.remove total +${totalMs}ms`, { id: String(id || "") });
  };

  const removeSeries = async (seriesId) => {
    const startedAt = getPerfNowMs();
    const mutationStartedAt = getPerfNowMs();
    await deleteSeriesAll(seriesId);
    const mutationMs = asIntMs(getPerfNowMs() - mutationStartedAt);
    console.info(`[perf] useEvents.removeSeries mutation.completed +${mutationMs}ms`, {
      seriesId: String(seriesId || ""),
    });

    setEvents((currentEvents) => {
      const next = currentEvents.filter(
        (eventItem) => String(eventItem.extendedProps?.series_id || "") !== String(seriesId || "")
      );
      writeCachedEvents(next);
      return next;
    });

    void load().catch(() => {});
    const totalMs = asIntMs(getPerfNowMs() - startedAt);
    console.info(`[perf] useEvents.removeSeries total +${totalMs}ms`, {
      seriesId: String(seriesId || ""),
    });
  };

  const removeSeriesFrom = async (seriesId, fromDate) => {
    const startedAt = getPerfNowMs();
    const mutationStartedAt = getPerfNowMs();
    await deleteSeriesFrom(seriesId, fromDate);
    const mutationMs = asIntMs(getPerfNowMs() - mutationStartedAt);
    console.info(`[perf] useEvents.removeSeriesFrom mutation.completed +${mutationMs}ms`, {
      seriesId: String(seriesId || ""),
      fromDate: String(fromDate || ""),
    });

    const fromDatePrefix = String(fromDate || "").slice(0, 10);
    setEvents((currentEvents) => {
      const next = currentEvents.filter((eventItem) => {
        const eventSeriesId = String(eventItem.extendedProps?.series_id || "");
        if (eventSeriesId !== String(seriesId || "")) {
          return true;
        }
        const eventDate = String(eventItem.start || "").slice(0, 10);
        return eventDate < fromDatePrefix;
      });
      writeCachedEvents(next);
      return next;
    });

    void load().catch(() => {});
    const totalMs = asIntMs(getPerfNowMs() - startedAt);
    console.info(`[perf] useEvents.removeSeriesFrom total +${totalMs}ms`, {
      seriesId: String(seriesId || ""),
      fromDate: String(fromDate || ""),
    });
  };

  useEffect(() => {
    load().catch(() => {
      setEvents((currentEvents) => (currentEvents.length > 0 ? currentEvents : readCachedEvents()));
    });
  }, [load]);

  useEffect(() => {
    if (!enabled || !getAuthToken()) {
      return undefined;
    }

    const unsubscribe = subscribeToEventUpdates(
      (update) => {
        if (update.type === "reload") {
          load();
          return;
        }
        setEvents((prev) => applyRealtimeUpdate(prev, update));
      },
      () => {
        load();
      }
    );

    return unsubscribe;
  }, [enabled, load, applyRealtimeUpdate]);

  return {
    events,
    create,
    createSilently,
    update,
    updateSilently,
    remove,
    removeSeries,
    removeSeriesFrom,
    reload: load,
  };
}
